import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useDataCache } from "../contexts/DataCacheContext";
import { batchDeleteJobs, listJobsByUser, listUsers } from "../lib/api";
import { JobRow } from "../components/JobRow";
import { Icon } from "../components/icons";
import type { Job, UserProfile } from "../lib/types";

type AdminUserGroup = {
  loaded: boolean;
  loading: boolean;
  jobs: Job[];
  cursor?: string;
};

export function Jobs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const {
    jobs,
    jobsLoading: loading,
    jobsError: error,
    refreshJobs: refresh,
    loadMoreJobs: loadMore,
    hasMoreJobs: hasMore,
    optimisticDelete,
    optimisticBatchRemove,
    optimisticCancel,
    averageGenTime,
  } = useDataCache();

  const [showAllJobs, setShowAllJobs] = useState(false);
  const [adminUsers, setAdminUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [userJobsByUserId, setUserJobsByUserId] = useState<Record<string, AdminUserGroup>>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [expandedAdminCards, setExpandedAdminCards] = useState<Set<string>>(new Set());
  const [initializedDefaults, setInitializedDefaults] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const sortByCreatedAt = (a: Job, b: Job) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const activeJobs = jobs
    .filter((j) => j.status === "queued" || j.status === "processing")
    .sort(sortByCreatedAt);
  const completedJobs = jobs
    .filter((j) => j.status !== "queued" && j.status !== "processing")
    .sort(sortByCreatedAt);

  // Single ordered list: active first (newest first), then history.
  const orderedJobs: Job[] = [...activeJobs, ...completedJobs];

  useEffect(() => {
    if (!initializedDefaults && completedJobs.length > 0) {
      setExpandedCards(new Set([completedJobs[0].jobId]));
      setInitializedDefaults(true);
    }
  }, [completedJobs, initializedDefaults]);

  useEffect(() => {
    for (const job of completedJobs) {
      if (job._prevStatus === "processing" && job.status === "complete") {
        setExpandedCards((prev) => new Set([...prev, job.jobId]));
      }
    }
  }, [completedJobs]);

  const toggleCard = (jobId: string) => {
    setExpandedCards((prev) => {
      if (prev.has(jobId)) return new Set<string>();
      return new Set([jobId]);
    });
  };

  const toggleAdminCard = (jobId: string) => {
    setExpandedAdminCards((prev) => {
      if (prev.has(jobId)) return new Set<string>();
      return new Set([jobId]);
    });
  };

  useEffect(() => {
    refresh();
  }, []);

  async function loadAdminUsers() {
    if (usersLoaded || usersLoading) return;
    setUsersLoading(true);
    try {
      const all: UserProfile[] = [];
      let cursor: string | undefined;
      do {
        const page = await listUsers(100, cursor);
        all.push(...page.items);
        cursor = page.cursor;
      } while (cursor);
      const others = all
        .filter((u) => u.userId !== user?.userId)
        .sort((a, b) => a.email.localeCompare(b.email));
      setAdminUsers(others);
      setUsersLoaded(true);
    } catch {
      // silently fail
    } finally {
      setUsersLoading(false);
    }
  }

  async function loadJobsForUser(userId: string, append = false) {
    const existing = userJobsByUserId[userId];
    if (existing?.loading) return;
    if (append && !existing?.cursor) return;

    setUserJobsByUserId((prev) => ({
      ...prev,
      [userId]: {
        loaded: existing?.loaded ?? false,
        loading: true,
        jobs: existing?.jobs ?? [],
        cursor: existing?.cursor,
      },
    }));

    try {
      const result = await listJobsByUser(
        userId,
        50,
        append ? existing?.cursor : undefined
      );
      setUserJobsByUserId((prev) => {
        const prior = prev[userId];
        const priorJobs = append ? prior?.jobs ?? [] : [];
        return {
          ...prev,
          [userId]: {
            loaded: true,
            loading: false,
            jobs: [...priorJobs, ...result.items],
            cursor: result.cursor,
          },
        };
      });
    } catch {
      setUserJobsByUserId((prev) => ({
        ...prev,
        [userId]: {
          loaded: prev[userId]?.loaded ?? false,
          loading: false,
          jobs: prev[userId]?.jobs ?? [],
          cursor: prev[userId]?.cursor,
        },
      }));
    }
  }

  const toggleGroup = (userId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
        if (!userJobsByUserId[userId]?.loaded) {
          loadJobsForUser(userId, false);
        }
      }
      return next;
    });
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedJobs(new Set());
  };

  const toggleJobSelection = (jobId: string) => {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const handleBatchDelete = async () => {
    if (selectedJobs.size === 0 || deleting) return;
    setDeleting(true);
    try {
      const jobIds = Array.from(selectedJobs);
      optimisticBatchRemove(jobIds);
      const result = await batchDeleteJobs(jobIds);
      if (result.failed.length > 0) {
        console.warn("Some deletions failed:", result.failed);
        refresh();
      }
      setSelectionMode(false);
      setSelectedJobs(new Set());
    } catch (err) {
      console.error("Batch delete failed:", err);
      refresh();
    } finally {
      setDeleting(false);
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="content">
        <div className="ph">
          <div>
            <div className="ph-eyebrow">Samples</div>
            <h1 className="ph-title">Generation history</h1>
            <div className="ph-lede">Loading…</div>
          </div>
        </div>
        <div className="stack-v">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-card" style={{ height: 80 }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="ph">
        <div>
          <div className="ph-eyebrow">Samples</div>
          <h1 className="ph-title">Generation history</h1>
          <div className="ph-lede">
            {jobs.length} sample{jobs.length === 1 ? "" : "s"}
            {activeJobs.length > 0 ? ` · ${activeJobs.length} active` : ""}.
            Newest first. Click any card to expand its takes.
          </div>
        </div>
        <div className="ph-actions">
          {selectionMode && selectedJobs.size > 0 && (
            <button
              type="button"
              className={`btn btn-danger${deleting ? " disabled" : ""}`}
              onClick={handleBatchDelete}
            >
              <Icon.Trash />
              {deleting ? "Deleting…" : `Delete (${selectedJobs.size})`}
            </button>
          )}
          <button
            type="button"
            className={`btn ${selectionMode ? "btn-line" : "btn-ghost"}`}
            onClick={toggleSelectionMode}
          >
            {selectionMode ? "Cancel" : "Select…"}
          </button>
          <button type="button" className="btn btn-ghost" onClick={refresh}>
            <Icon.Refresh /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="err-banner">{error}</div>}

      {jobs.length === 0 && (
        <div className="empty-state">
          <div className="ico"><Icon.Music /></div>
          <h3>No samples yet</h3>
          <p>Generate speech or convert a voice to get started.</p>
        </div>
      )}

      {/* Unified history */}
      {orderedJobs.length > 0 && (
        <div className="stack-v">
          <h3
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 10.5,
              color: "var(--ink-3)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              margin: "0 0 4px",
            }}
          >
            History ({orderedJobs.length})
          </h3>
          {orderedJobs.map((job) => {
            const active = job.status === "queued" || job.status === "processing";
            return (
              <JobRow
                key={job.jobId}
                job={job}
                onRefresh={refresh}
                onDelete={active ? undefined : optimisticDelete}
                onCancel={active ? optimisticCancel : undefined}
                averageGenTime={averageGenTime}
                collapsed={!expandedCards.has(job.jobId)}
                onToggleCollapse={() => toggleCard(job.jobId)}
                selectionMode={selectionMode}
                selected={selectedJobs.has(job.jobId)}
                onToggleSelection={() => toggleJobSelection(job.jobId)}
              />
            );
          })}
        </div>
      )}

      {hasMore && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 18 }}>
          <button type="button" className="btn btn-line" onClick={loadMore}>
            Load more
          </button>
        </div>
      )}

      {/* Admin: All Users' Samples */}
      {isAdmin && (
        <div className="card" style={{ marginTop: 18, padding: 0, overflow: "hidden" }}>
          <button
            type="button"
            onClick={() => {
              setShowAllJobs(!showAllJobs);
              if (!showAllJobs) loadAdminUsers();
            }}
            style={{
              width: "100%",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "transparent",
              transition: "background .15s",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--amber)", fontSize: 13, fontWeight: 500 }}>
              <Icon.Users /> All users' samples
            </span>
            <Icon.Caret
              width={14}
              height={14}
              style={{
                color: "var(--ink-3)",
                transform: showAllJobs ? "rotate(180deg)" : "none",
                transition: "transform .2s",
                flexShrink: 0,
              }}
            />
          </button>

          {showAllJobs && (
            <div
              style={{
                padding: "12px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                borderTop: "1px solid var(--hair)",
              }}
            >
              {usersLoading && adminUsers.length === 0 ? (
                <div className="stack-v">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton-card" style={{ height: 40 }} />
                  ))}
                </div>
              ) : adminUsers.length === 0 ? (
                <p style={{ fontSize: 13, color: "var(--ink-3)" }}>No other users.</p>
              ) : (
                adminUsers.map((u) => {
                  const group = userJobsByUserId[u.userId];
                  const expanded = expandedGroups.has(u.userId);
                  return (
                    <div
                      key={u.userId}
                      style={{
                        border: "1px solid var(--hair)",
                        borderRadius: 10,
                        overflow: "hidden",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleGroup(u.userId)}
                        style={{
                          width: "100%",
                          padding: "10px 14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: "var(--bg-1)",
                        }}
                      >
                        <span style={{ fontSize: 12, color: "var(--ink-2)" }}>
                          {u.email} <span style={{ color: "var(--ink-4)" }}>({u.jobCount} {u.jobCount === 1 ? "sample" : "samples"})</span>
                        </span>
                        <Icon.Caret
                          width={14}
                          height={14}
                          style={{
                            color: "var(--ink-3)",
                            transform: expanded ? "rotate(180deg)" : "none",
                            transition: "transform .2s",
                            flexShrink: 0,
                          }}
                        />
                      </button>
                      {expanded && (
                        <div
                          style={{
                            padding: 12,
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            borderTop: "1px solid var(--hair)",
                          }}
                        >
                          {group?.loading && !group.loaded ? (
                            <>
                              {[1, 2].map((i) => (
                                <div key={i} className="skeleton-card" style={{ height: 80 }} />
                              ))}
                            </>
                          ) : group?.loaded && group.jobs.length === 0 ? (
                            <p style={{ fontSize: 12, color: "var(--ink-3)" }}>No samples.</p>
                          ) : (
                            <>
                              {group?.jobs.map((job) => (
                                <JobRow
                                  key={job.jobId}
                                  job={job}
                                  onRefresh={() => loadJobsForUser(u.userId, false)}
                                  showUserId
                                  collapsed={!expandedAdminCards.has(job.jobId)}
                                  onToggleCollapse={() => toggleAdminCard(job.jobId)}
                                  selectionMode={false}
                                  selected={false}
                                  onToggleSelection={() => {}}
                                />
                              ))}
                              {group?.cursor && (
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  style={{ alignSelf: "center" }}
                                  onClick={() => loadJobsForUser(u.userId, true)}
                                  disabled={group.loading}
                                >
                                  {group.loading ? "Loading…" : "Load more"}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
